import { useState } from "react";
import { Ban, CheckCircle, AlertTriangle, XCircle, Loader2, Search, Globe, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { EmailResultsButton } from "@/components/EmailResultsButton";

interface BlacklistCheckerProps {
  userEmail?: string | null;
  userName?: string | null;
  sessionToken?: string | null;
}

interface BlacklistResult {
  name: string;
  listed: boolean;
  description: string;
  category: "spam" | "malware" | "phishing" | "general";
}

interface CheckResult {
  query: string;
  queryType: "ip" | "domain";
  resolvedIP?: string;
  results: BlacklistResult[];
  listedCount: number;
  totalChecked: number;
  status: "clean" | "warning" | "critical";
}

export const BlacklistChecker = ({ userEmail, userName, sessionToken }: BlacklistCheckerProps) => {
  const [query, setQuery] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<CheckResult | null>(null);

  const checkBlacklist = async () => {
    if (!query.trim()) {
      toast.error("Please enter a domain or IP address");
      return;
    }

    setIsChecking(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("check-blacklist", {
        body: { query: query.trim() },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setResult(data);
    } catch (error) {
      console.error("Error checking blacklist:", error);
      toast.error("Failed to check blacklists. Please try again.");
    } finally {
      setIsChecking(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "clean":
        return {
          icon: <CheckCircle className="w-6 h-6 text-success" />,
          bg: "bg-success/10 border-success/20",
          text: "text-success",
          label: "Clean"
        };
      case "warning":
        return {
          icon: <AlertTriangle className="w-6 h-6 text-warning" />,
          bg: "bg-warning/10 border-warning/20",
          text: "text-warning",
          label: "Warning"
        };
      case "critical":
        return {
          icon: <XCircle className="w-6 h-6 text-destructive" />,
          bg: "bg-destructive/10 border-destructive/20",
          text: "text-destructive",
          label: "Critical"
        };
      default:
        return {
          icon: null,
          bg: "bg-muted",
          text: "text-muted-foreground",
          label: "Unknown"
        };
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "spam": return "bg-orange-500/10 text-orange-500";
      case "malware": return "bg-red-500/10 text-red-500";
      case "phishing": return "bg-purple-500/10 text-purple-500";
      default: return "bg-blue-500/10 text-blue-500";
    }
  };

  const listedResults = result?.results.filter(r => r.listed) || [];
  const cleanResults = result?.results.filter(r => !r.listed) || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Ban className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Blacklist Checker
          </h3>
          <p className="text-sm text-muted-foreground">
            Check if a domain or IP is listed on 12 major email blocklists
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium text-foreground mb-1.5 block">
            Domain or IP Address
          </label>
          <div className="flex gap-3">
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setResult(null);
              }}
              placeholder="example.com or 192.168.1.1"
              onKeyDown={(e) => e.key === "Enter" && checkBlacklist()}
              className="flex-1"
            />
            <Button
              onClick={checkBlacklist}
              disabled={isChecking || !query.trim()}
            >
              {isChecking ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {isChecking && (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-3" />
            <p className="text-sm text-muted-foreground">
              Checking {result?.totalChecked || 12} blacklists...
            </p>
          </div>
        )}

        {result && !isChecking && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Email Results Button */}
            <div className="flex justify-end">
              <EmailResultsButton
                toolId="blacklist-checker"
                toolName="Blacklist Checker"
                results={result}
                userEmail={userEmail || null}
                userName={userName || null}
                sessionToken={sessionToken || null}
              />
            </div>

            {/* Status Card */}
            <div className={`rounded-xl border p-4 ${getStatusConfig(result.status).bg}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getStatusConfig(result.status).icon}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {result.queryType === "domain" ? (
                        <Globe className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Server className="w-4 h-4 text-muted-foreground" />
                      )}
                      <span className="font-medium text-foreground">{result.query}</span>
                    </div>
                    {result.resolvedIP && (
                      <p className="text-xs text-muted-foreground">
                        Resolved IP: {result.resolvedIP}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${getStatusConfig(result.status).text}`}>
                    {result.listedCount}/{result.totalChecked}
                  </p>
                  <p className="text-xs text-muted-foreground">blacklists</p>
                </div>
              </div>
            </div>

            {/* Listed Results */}
            {listedResults.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-destructive" />
                  Listed on {listedResults.length} blacklist{listedResults.length > 1 ? "s" : ""}
                </h4>
                <div className="space-y-2">
                  {listedResults.map((bl, i) => (
                    <div 
                      key={i}
                      className="bg-destructive/5 border border-destructive/20 rounded-lg p-3 flex items-start justify-between gap-3"
                    >
                      <div>
                        <p className="font-medium text-foreground text-sm">{bl.name}</p>
                        <p className="text-xs text-muted-foreground">{bl.description}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(bl.category)}`}>
                        {bl.category}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Clean Results */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-success" />
                Clean on {cleanResults.length} blacklist{cleanResults.length > 1 ? "s" : ""}
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {cleanResults.map((bl, i) => (
                  <div 
                    key={i}
                    className="bg-muted/50 rounded-lg px-3 py-2 text-xs"
                  >
                    <p className="font-medium text-foreground truncate">{bl.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            {result.status !== "clean" && (
              <div className="bg-warning/5 border border-warning/20 rounded-xl p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">What to do if you're listed:</h4>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  <li>• Visit each blacklist's website for delisting instructions</li>
                  <li>• Investigate the source of spam (compromised accounts, open relays)</li>
                  <li>• Ensure proper email authentication (SPF, DKIM, DMARC)</li>
                  <li>• Consider using a dedicated IP for important email</li>
                  <li>• Monitor your sending reputation regularly</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
